import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Copy, Phone, Mail } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type IconType = 'phone' | 'email' | 'copy';

const iconMap = {
  phone: Phone,
  email: Mail,
  copy: Copy,
};

interface CopyButtonProps {
  value: string;
  label?: string;
  icon?: IconType;
  variant?: 'outline' | 'ghost' | 'secondary';
  size?: 'sm' | 'default' | 'icon';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export function CopyButton({
  value,
  label,
  icon = 'copy',
  variant = 'outline',
  size = 'sm',
  className,
  onClick,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: do nothing
    }
  }, [value, onClick]);

  const Icon = copied ? Check : iconMap[icon];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={variant}
            size={size}
            className={className}
            onClick={handleCopy}
          >
            <Icon className="w-4 h-4" />
            {label && <span className="ml-1">{label}</span>}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{copied ? 'Copied!' : `Copy ${icon === 'phone' ? 'phone number' : icon === 'email' ? 'email' : value}`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
